import SmsCombinedPanel from "@/components/SmsCombinedPanel";
/**
 * APX WorkCenter — Tax Division Manager Dashboard (Jasmin)
 * Design: "Gold Standard" — Premium Editorial Finance
 * Purpose:
 *   - Receive tasks assigned by Controllers (Gus / Amy) with status tracking
 *   - Request documents from Jany or bookkeepers
 *   - Receive files via the hub file transfer system
 */

import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { trpc } from "@/lib/trpc";
import AllCommunicationsFeed from "@/components/AllCommunicationsFeed";
import { toast } from "sonner";
import ClientDocuments from "@/components/ClientDocuments";
import { ContactClientModal } from "@/components/ContactClientModal";
import SendNotificationModal from "@/components/SendNotificationModal";
import FileDropZone from "@/components/FileDropZone";
import { useLocation } from "wouter";
import MyTasksPanel from "@/components/MyTasksPanel";

// ─── Types ────────────────────────────────────────────────────────────────────
type TaskStatus = "not_started" | "in_progress" | "complete";

const STATUS_CONFIG: Record<TaskStatus, { label: string; bg: string; color: string; border: string }> = {
  not_started: {
    label: "Not Started",
    bg: "oklch(0.2 0.04 240 / 60%)",
    color: "oklch(0.22 0.04 240)",
    border: "oklch(0.3 0.04 240)",
  },
  in_progress: {
    label: "In Progress",
    bg: "oklch(0.92 0.005 240 / 15%)",
    color: "oklch(0.08 0.01 240)",
    border: "oklch(0.92 0.005 240 / 40%)",
  },
  complete: {
    label: "Complete",
    bg: "oklch(0.92 0.005 240 / 15%)",
    color: "oklch(0.08 0.01 240)",
    border: "oklch(0.92 0.005 240 / 40%)",
  },
};

// ─── Document Request Recipients ─────────────────────────────────────────────
const REQUEST_RECIPIENTS = [
  { label: "Jany L. (Bookkeeping Manager)", value: "email:lopez.jany@gmail.com", name: "Jany L." },
  { label: "All Bookkeepers", value: "role:bookkeeper", name: "All Bookkeepers" },
  { label: "Irit P.", value: "email:iritna@gmail.com", name: "Irit P." },
  { label: "Yunuen T.", value: "email:yunuenalfaro7@gmail.com", name: "Yunuen T." },
  { label: "Shobhit A.", value: "email:shobhit@advancefinserv.com", name: "Shobhit A." },
  { label: "Lulu M.", value: "email:lourdesmastache20@gmail.com", name: "Lulu M." },
  { label: "Marian A.", value: "email:marianjoyhernandez2@gmail.com", name: "Marian A." },
  { label: "Renata L.", value: "email:erenatalazo@gmail.com", name: "Renata L." },
  { label: "Arvind K.", value: "email:arvind@advancefinserv.com", name: "Arvind K." },
];

const QUICK_REQUESTS = [
  "W-2 / W-9 forms",
  "Prior year financials",
  "Bank statements (Q4)",
  "1099 forms",
  "Depreciation schedule",
  "Fixed asset list",
  "Loan statements",
  "Payroll summary",
  "Charitable contribution receipts",
  "Business expense receipts",
];

// ─── Task Board (assigned by Controllers) ────────────────────────────────────────────────────────────────────────────────
function TaskBoard() {
  const utils = trpc.useUtils();

  const { data: tasks, isLoading } = trpc.taxTasks.list.useQuery(undefined, {
    refetchInterval: 30000,
  });

  const updateStatus = trpc.taxTasks.updateStatus.useMutation({
    onSuccess: () => {
      utils.taxTasks.list.invalidate();
      toast.success("Status updated");
    },
    onError: (err) => toast.error("Failed to update status", { description: err.message }),
  });

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-16 rounded-lg animate-pulse" style={{ background: "oklch(0.94 0.018 220)" }} />
        ))}
      </div>
    );
  }

  if (!tasks?.length) {
    return (
      <div
        className="rounded-xl px-5 py-8 text-center"
        style={{ background: "oklch(0.94 0.018 220)", border: "2px solid oklch(0.25 0.04 240)" }}
      >
        <p style={{ color: "oklch(0.28 0.03 240)", fontSize: "0.85rem" }}>
          No tasks assigned yet. Gus or Amy will push tasks here when needed.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {tasks.map((task) => {
        const currentStatus = task.status as TaskStatus;
        const cfg = STATUS_CONFIG[currentStatus] ?? STATUS_CONFIG.not_started;

        return (
          <div
            key={task.id}
            className="rounded-xl px-4 py-3"
            style={{ background: "oklch(0.94 0.018 220)", border: "2px solid oklch(0.25 0.04 240)" }}
          >
            <div className="flex items-start justify-between gap-3">
              {/* Task info */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p style={{ color: "oklch(0.12 0.04 240)", fontSize: "0.82rem", fontWeight: 600 }}>
                    {task.title}
                  </p>
                  <span
                    className="text-sm px-2 py-0.5 rounded-full font-medium"
                    style={{ background: "oklch(0.92 0.005 240 / 10%)", color: "oklch(0.08 0.01 240)", border: "2px solid oklch(0.25 0.04 240)" }}
                  >
                    {task.clientName}
                  </span>
                </div>
                <p style={{ color: "oklch(0.22 0.04 240)", fontSize: "0.875rem", marginTop: "0.15rem" }}>
                  From {task.assignedBy} &middot;{" "}
                  {new Date(task.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  {task.dueDate && (
                    <span style={{ color: "oklch(0.08 0.01 240)", marginLeft: "0.5rem" }}>
                      Due: {task.dueDate}
                    </span>
                  )}
                </p>
                {task.notes && (
                  <p style={{ color: "oklch(0.14 0.04 240)", fontSize: "0.875rem", marginTop: "0.25rem", lineHeight: 1.4 }} className="line-clamp-2">
                    {task.notes}
                  </p>
                )}
              </div>

              {/* Status selector */}
              <div className="shrink-0">
                <select
                  value={currentStatus}
                  onChange={(e) => updateStatus.mutate({ id: task.id, status: e.target.value as TaskStatus })}
                  disabled={updateStatus.isPending}
                  className="text-sm font-semibold rounded-lg px-2 py-1.5 cursor-pointer outline-none"
                  style={{
                    background: cfg.bg,
                    color: cfg.color,
                    border: `1px solid ${cfg.border}`,
                    minWidth: "110px",
                  }}
                >
                  {(Object.entries(STATUS_CONFIG) as [TaskStatus, typeof STATUS_CONFIG[TaskStatus]][]).map(([key, val]) => (
                    <option key={key} value={key} style={{ background: "oklch(0.94 0.018 220)", color: "oklch(0.12 0.04 240)" }}>
                      {val.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Document Request Form ────────────────────────────────────────────────────
function DocumentRequestForm() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const [recipient, setRecipient] = useState("");
  const [clientName, setClientName] = useState("");
  const [docType, setDocType] = useState("");
  const [notes, setNotes] = useState("");
  const [urgency, setUrgency] = useState<"normal" | "urgent">("normal");

  const sendNotification = trpc.notifications.send.useMutation({
    onSuccess: () => {
      toast.success("Document request sent", {
        description: `${REQUEST_RECIPIENTS.find((r) => r.value === recipient)?.name ?? recipient} has been notified.`,
      });
      setRecipient("");
      setClientName("");
      setDocType("");
      setNotes("");
      setUrgency("normal");
    },
    onError: (err) => toast.error("Failed to send request", { description: err.message }),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!recipient || !clientName || !docType) return;

    const isRole = recipient.startsWith("role:");
    const urgencyTag = urgency === "urgent" ? " ⚠️ URGENT" : "";
    const title = `Document Request${urgencyTag} — ${clientName}`;
    const message = `Jasmin (Tax Division) is requesting: **${docType}** for client **${clientName}**.${notes ? `\n\nAdditional notes: ${notes}` : ""}\n\nPlease upload the document to the hub file transfer when ready.`;

    sendNotification.mutate({
      senderName: user?.name ?? "Jasmin",
      senderRole: "tax_division",
      recipientEmail: isRole ? undefined : recipient.replace("email:", ""),
      recipientRole: isRole ? recipient.replace("role:", "") : undefined,
      title,
      message,
      type: urgency === "urgent" ? "alert" : "task",
    });
  };

  const s = {
    label: { color: "oklch(0.22 0.04 240)", fontSize: "0.875rem", textTransform: "uppercase" as const, letterSpacing: "0.06em", fontWeight: 600, marginBottom: "0.4rem", display: "block" },
    input: { background: "oklch(0.95 0.006 240)", border: "2px solid oklch(0.25 0.04 240)", borderRadius: "0.5rem", padding: "0.6rem 0.75rem", color: "oklch(0.12 0.04 240)", fontSize: "0.875rem", width: "100%", outline: "none" },
    select: { background: "oklch(0.95 0.006 240)", border: "2px solid oklch(0.25 0.04 240)", borderRadius: "0.5rem", padding: "0.6rem 0.75rem", color: "oklch(0.12 0.04 240)", fontSize: "0.875rem", width: "100%", outline: "none" },
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label style={s.label}>Send Request To</label>
        <select value={recipient} onChange={(e) => setRecipient(e.target.value)} required style={s.select}>
          <option value="">Select recipient…</option>
          {REQUEST_RECIPIENTS.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label style={s.label}>Client Name</label>
        <input
          type="text"
          value={clientName}
          onChange={(e) => setClientName(e.target.value)}
          placeholder="e.g. Sunrise Café LLC"
          required
          style={s.input}
        />
      </div>

      <div>
        <label style={s.label}>Document Requested</label>
        <input
          type="text"
          value={docType}
          onChange={(e) => setDocType(e.target.value)}
          placeholder="e.g. Bank statements (Q4 2025)"
          required
          list="doc-suggestions"
          style={s.input}
        />
        <datalist id="doc-suggestions">
          {QUICK_REQUESTS.map((d) => <option key={d} value={d} />)}
        </datalist>
      </div>

      <div>
        <label style={s.label}>Priority</label>
        <div className="flex gap-2">
          {(["normal", "urgent"] as const).map((u) => (
            <button
              key={u}
              type="button"
              onClick={() => setUrgency(u)}
              className="px-4 py-1.5 rounded-lg text-sm font-semibold capitalize transition-all duration-150"
              style={{
                background: urgency === u
                  ? u === "urgent" ? "oklch(0.92 0.005 240 / 20%)" : "oklch(0.92 0.005 240 / 15%)"
                  : "oklch(0.95 0.006 240)",
                border: `1px solid ${urgency === u
                  ? u === "urgent" ? "oklch(0.92 0.005 240 / 50%)" : "oklch(0.92 0.005 240 / 40%)"
                  : "oklch(1 0 0 / 10%)"}`,
                color: urgency === u
                  ? u === "urgent" ? "oklch(0.82 0.01 240)" : "oklch(0.82 0.01 240)"
                  : "oklch(0.5 0.02 240)",
              }}
            >
              {u === "urgent" ? "⚠️ Urgent" : "Normal"}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label style={s.label}>Additional Notes (optional)</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Any specific instructions or context…"
          rows={2}
          style={{ ...s.input, resize: "none" }}
        />
      </div>

      <button
        type="submit"
        disabled={sendNotification.isPending}
        className="w-full py-2.5 rounded-lg text-sm font-bold transition-all duration-150"
        style={{
          background: "linear-gradient(135deg, oklch(0.82 0.01 240), oklch(0.55 0.01 240))",
          color: "oklch(0.12 0.04 240)",
          opacity: sendNotification.isPending ? 0.7 : 1,
        }}
      >
        {sendNotification.isPending ? "Sending…" : "Send Document Request"}
      </button>
    </form>
  );
}

// ─── Main Dashboard ─────────────────────────────────────────────────────────────────
export default function TaxDivisionDashboard() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const [activeTab, setActiveTab] = useState<"tasks" | "request" | "inbox" | "drive">("tasks");
  const [driveClient, setDriveClient] = useState<string | null>(null);
  const [contactModal, setContactModal] = useState<{ clientName: string; clientEmail?: string | null; clientPhone?: string | null } | null>(null);
  const [showNotify, setShowNotify] = useState(false);
  const [showFiles, setShowFiles] = useState(false);

  const tabs = [
    { key: "tasks" as const, label: "My Tasks" },
    { key: "request" as const, label: "Request Document" },
    { key: "inbox" as const, label: "Document Inbox" },
    { key: "drive" as const, label: "Drive Docs" },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-slide-up">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <span
                className="text-sm font-semibold px-2.5 py-1 rounded-full uppercase tracking-wider"
                style={{ background: "oklch(0.92 0.005 240 / 15%)", color: "oklch(0.08 0.01 240)", border: "2px solid oklch(0.25 0.04 240)" }}
              >
                Tax Division
              </span>
            </div>
            <h1
              className="text-2xl font-bold text-[oklch(0.12_0.04_240)]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Welcome, {user?.name}
            </h1>
            <p className="text-sm text-[oklch(0.08_0.01_240)] mt-0.5">
              Manage tasks from the Controllers, request documents, and receive files — all in one place.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap shrink-0">
            {/* Notify Client */}
            <button
              onClick={() => setContactModal({ clientName: "", clientEmail: null, clientPhone: null })}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-150"
              style={{ background: "oklch(0.92 0.005 240 / 15%)", border: "2px solid oklch(0.25 0.04 240)", color: "oklch(0.08 0.01 240)" }}
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Notify Client
            </button>
            {/* Notify Team */}
            <button
              onClick={() => setShowNotify(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-150"
              style={{ background: "oklch(0.92 0.005 240 / 15%)", border: "2px solid oklch(0.25 0.04 240)", color: "oklch(0.08 0.01 240)" }}
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              Notify Team
            </button>
            {/* Send Files */}
            <button
              onClick={() => setShowFiles(v => !v)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-150"
              style={{ background: "oklch(0.92 0.005 240 / 15%)", border: "2px solid oklch(0.25 0.04 240)", color: "oklch(0.08 0.01 240)" }}
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              {showFiles ? "Hide Files" : "Send Files"}
            </button>
          </div>
        </div>

        {/* ── File panel ── */}
        {showFiles && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="apx-card rounded-xl p-5">
              <h3 className="text-sm font-semibold text-[oklch(0.08_0.01_240)] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Send Files</h3>
              <FileDropZone />
            </div>
            
          </div>
        )}

        {/* ── My Tasks Panel ── */}
        <div className="apx-card rounded-xl p-5">
          <MyTasksPanel />
        </div>
        {/* ── Two-column body ── */}
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-5 items-start">

          {/* LEFT: Tabs + info banner */}
          <div className="space-y-5">

        {/* Tab navigation */}
        <div className="flex gap-1 p-1 rounded-xl" style={{ background: "oklch(0.94 0.018 220)", width: "fit-content" }}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => tab.key === "drive" ? navigate("/drive-docs") : setActiveTab(tab.key)}
              className="px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150"
              style={{
                background: activeTab === tab.key ? "oklch(0.82 0.01 240)" : "transparent",
                color: activeTab === tab.key ? "oklch(0.08 0.01 240)" : "oklch(0.35 0.02 240)",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === "drive" && (
          <div className="apx-card rounded-xl p-5">
            <h2
              className="text-base font-bold mb-1"
              style={{ fontFamily: "'Playfair Display', serif", color: "oklch(0.08 0.01 240)" }}
            >
              Client Drive Documents
            </h2>
            <p className="text-sm mb-4" style={{ color: "oklch(0.22 0.04 240)" }}>
              Look up any client's Google Drive folder. Type a client name to browse their documents.
            </p>
            {/* Client name search */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="Enter client name (e.g. CLOVIC CONSTRUCTION)"
                defaultValue={driveClient ?? ""}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const val = (e.target as HTMLInputElement).value.trim();
                    if (val) setDriveClient(val);
                  }
                }}
                onBlur={(e) => {
                  const val = e.target.value.trim();
                  if (val) setDriveClient(val);
                }}
                className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                style={{
                  background: "oklch(0.93 0.008 240)",
                  border: "1px solid oklch(0.88 0.01 240)",
                  color: "oklch(0.12 0.04 240)",
                }}
              />
              <p className="text-sm mt-1" style={{ color: "oklch(0.28 0.03 240)" }}>
                Press Enter or click away to search
              </p>
            </div>
            {driveClient && (
              <ClientDocuments clientName={driveClient} />
            )}
          </div>
        )}

        {activeTab === "tasks" && (
          <div className="apx-card rounded-xl p-5">
            <h2
              className="text-base font-bold mb-1"
              style={{ fontFamily: "'Playfair Display', serif", color: "oklch(0.08 0.01 240)" }}
            >
              Tasks from Controllers
            </h2>
            <p className="text-sm mb-4" style={{ color: "oklch(0.22 0.04 240)" }}>
              Tasks assigned by Gus or Amy appear here. Update the status as you work through each one.
            </p>
            <TaskBoard />
          </div>
        )}

        {activeTab === "request" && (
          <div className="apx-card rounded-xl p-5">
            <h2
              className="text-base font-bold mb-1"
              style={{ fontFamily: "'Playfair Display', serif", color: "oklch(0.08 0.01 240)" }}
            >
              Request a Document
            </h2>
            <p className="text-sm mb-4" style={{ color: "oklch(0.22 0.04 240)" }}>
              Send a document request directly to Jany or a bookkeeper. They will receive a notification and upload the file to your inbox.
            </p>
            <DocumentRequestForm />
          </div>
        )}

        {activeTab === "inbox" && (
          <div className="apx-card rounded-xl p-5">
            <h2
              className="text-base font-bold mb-1"
              style={{ fontFamily: "'Playfair Display', serif", color: "oklch(0.08 0.01 240)" }}
            >
              All Communications
            </h2>
            <p className="text-sm mb-4" style={{ color: "oklch(0.22 0.04 240)" }}>
              All files, notifications, and messages sent to you appear here in one place.
            </p>
            <AllCommunicationsFeed
              email={user?.email ?? ""}
              role={user?.role ?? "tax_division"}
              userName={user?.name ?? "Jasmin"}
              userRole={user?.role ?? "tax_division"}
            />
          </div>
        )}



          </div>{/* end left column */}

          {/* RIGHT: Schedule + Inbox — sticky */}
          <div className="xl:sticky xl:top-6 space-y-5">

          </div>{/* end right column */}

        </div>{/* end two-column grid */}

        {/* ── BOTTOM: SMS (left) + All Communications (right) ── */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 mt-5">
          <div className="apx-card rounded-xl overflow-hidden">
            <SmsCombinedPanel />
          </div>
          <div className="apx-card rounded-xl overflow-hidden p-4" style={{ minHeight: 420 }}>
            <AllCommunicationsFeed
              email={user?.email ?? ""}
              role={user?.role ?? "tax_division"}
              userName={user?.name ?? "Jasmin"}
              userRole={user?.role ?? "tax_division"}
            />
          </div>
        </div>

      </div>

      {showNotify && <SendNotificationModal onClose={() => setShowNotify(false)} />}

      {/* Contact Client Modal */}
      {contactModal !== null && (
        <ContactClientModal
          open={!!contactModal}
          onClose={() => setContactModal(null)}
          clientName={contactModal.clientName}
          clientEmail={contactModal.clientEmail}
          clientPhone={contactModal.clientPhone}
          senderName={user?.name ?? "APX Team"}
          senderEmail={user?.email ?? ""}
          userRole={user?.role ?? "tax_division"}
        />
      )}
    </DashboardLayout>
  );
}
